import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useWorldData } from '../../hooks/useWorldData';
import { WorldSidebar, type WorldSection } from '../../components/admin/world/WorldSidebar';
import { WorldOverview } from '../../components/admin/world/WorldOverview';
import { WorldCanon } from '../../components/admin/world/WorldCanon';
import { WorldCorrespondent } from '../../components/admin/world/WorldCorrespondent';
import { WorldConfiguration } from '../../components/admin/world/WorldConfiguration';
import { WorldHistory } from '../../components/admin/world/WorldHistory';
import { WorldOperations } from '../../components/admin/world/WorldOperations';
import { WorldGraph } from '../../components/admin/WorldGraph';
import { LoadingSkeleton } from '../../components/admin/LoadingSkeleton';

function parseSection(hash: string): WorldSection {
  const section = hash.replace('#', '').split('/')[0];
  const validSections: WorldSection[] = ['overview', 'canon', 'graph', 'correspondent', 'configuration', 'history', 'operations'];
  return validSections.includes(section as WorldSection) ? (section as WorldSection) : 'overview';
}

export function WorldPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const versionParam = searchParams.get('version');
  const initialVersion = versionParam ? parseInt(versionParam, 10) : null;

  const {
    world,
    state,
    evolutions,
    visuals,
    versions,
    previousState,
    isLoading,
    error,
    refetchWorld,
    refetchState,
    setStateVersion,
    currentVersion,
  } = useWorldData(id, initialVersion);

  const [activeSection, setActiveSection] = useState<WorldSection>(() => parseSection(location.hash));
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [lightbox, setLightbox] = useState<{ url: string; prompt?: string | null } | null>(null);

  // Sync section from URL hash
  useEffect(() => {
    const section = parseSection(location.hash);
    setActiveSection(section);
  }, [location.hash]);

  // Handle section change - update URL hash
  const handleSectionChange = (section: WorldSection) => {
    setActiveSection(section);
    window.history.replaceState(null, '', `#${section}`);
  };

  // Handle version change - update URL param
  const handleVersionChange = (version: number | null) => {
    setStateVersion(version);
    if (version) {
      setSearchParams({ version: version.toString() });
    } else {
      setSearchParams({});
    }
  };

  // Handle thumbnail click - open lightbox
  const handleThumbnailClick = () => {
    const thumbnailUrl = state?.thumbnailUrl ?? world?.latestState?.thumbnailUrl;
    if (thumbnailUrl) {
      setLightbox({
        url: thumbnailUrl,
        prompt: state?.thumbnailPrompt ?? world?.latestState?.thumbnailPrompt,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-5rem)]">
        <div className="w-72 border-r border-stone/30 p-4 hidden lg:block">
          <LoadingSkeleton variant="card" />
        </div>
        <div className="flex-1 p-6">
          <LoadingSkeleton variant="card" />
          <div className="mt-6">
            <LoadingSkeleton variant="card" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-ember mb-4">{error}</div>
        <Link to="/admin/worlds" className="text-rune hover:underline">
          Back to Worlds
        </Link>
      </div>
    );
  }

  if (!world) return null;

  return (
    <div className="flex h-[calc(100vh-5rem)] -m-4 sm:-m-6">
      {/* Sidebar */}
      <WorldSidebar
        world={world}
        state={state}
        versions={versions}
        currentVersion={currentVersion}
        onVersionChange={handleVersionChange}
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        onThumbnailClick={handleThumbnailClick}
      />

      {/* Main content */}
      <main className={`flex-1 overflow-y-auto ${activeSection === 'graph' ? 'flex flex-col' : ''}`}>
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-30 bg-night border-b border-stone/30 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 text-mist hover:text-parchment hover:bg-stone/30 rounded-lg transition-colors"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-sm text-parchment truncate">{world.inboundToken}</div>
            <div className="text-xs text-mist">Day {world.dayIndex}</div>
          </div>
        </div>

        {/* Graph section - full height, no padding */}
        {activeSection === 'graph' && state && (
          <div className="flex-1 min-h-0">
            <WorldGraph
              data={
                state.canon as {
                  entities: {
                    characters: Record<string, { name: string; status?: string; traits?: string[] }>;
                    places: Record<string, { name: string; description?: string }>;
                    factions: Record<string, { name: string; description?: string }>;
                    items: Record<string, { name: string; description?: string }>;
                  };
                  relationships: Array<{ a: string; b: string; type: string; strength: number }>;
                  timeline: {
                    t_current: string;
                    events: Record<string, { title: string; participants: string[]; day_index?: number }>;
                  };
                  threads: {
                    open: Record<string, { title: string; stakes?: string }>;
                    closed: Record<string, { title: string; resolution?: string }>;
                  };
                }
              }
            />
          </div>
        )}

        {activeSection === 'graph' && !state && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="text-mist mb-2">No world state exists yet.</div>
              <div className="text-mist/60 text-sm">
                World state is created when the first letter is processed or when the world evolves.
              </div>
            </div>
          </div>
        )}

        {/* Section content - with padding */}
        {activeSection !== 'graph' && (
          <div className="p-4 sm:p-6">
            {activeSection === 'overview' && (
              <WorldOverview
                world={world}
                state={state}
                evolutions={evolutions}
                onNavigate={handleSectionChange}
              />
            )}

            {activeSection === 'canon' && state && (
              <WorldCanon
                state={state}
                subSection={location.hash.includes('/') ? location.hash.split('/')[1] : undefined}
              />
            )}

            {activeSection === 'correspondent' && state && (
              <WorldCorrespondent
                state={state}
                previousState={previousState}
              />
            )}

            {activeSection === 'configuration' && (
              <WorldConfiguration
                world={world}
                state={state}
                onUpdate={async () => {
                  await refetchWorld();
                  await refetchState();
                }}
              />
            )}

            {activeSection === 'history' && (
              <WorldHistory
                world={world}
                state={state}
                evolutions={evolutions}
                visuals={visuals}
              />
            )}

            {activeSection === 'operations' && (
              <WorldOperations
                world={world}
                onUpdate={async () => {
                  await refetchWorld();
                  await refetchState();
                }}
              />
            )}

            {/* Show message when section requires state but none exists */}
            {(activeSection === 'canon' || activeSection === 'correspondent') && !state && (
              <div className="text-center py-12">
                <div className="text-mist mb-2">No world state exists yet.</div>
                <div className="text-mist/60 text-sm">
                  World state is created when the first letter is processed or when the world evolves.
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-void/80 backdrop-blur-sm"
            onClick={() => setLightbox(null)}
          />
          <div className="relative max-h-[90vh] max-w-[90vw]">
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-4 -right-4 bg-void text-mist hover:text-parchment rounded-full border border-stone/40 w-8 h-8 flex items-center justify-center"
              aria-label="Close image"
            >
              ×
            </button>
            <div className="flex flex-col gap-3 max-h-[90vh] max-w-[90vw]">
              <img
                src={lightbox.url}
                alt="World thumbnail"
                className="max-h-[75vh] max-w-[90vw] rounded-lg object-contain bg-parchment/95"
              />
              {lightbox.prompt && (
                <div className="bg-void/95 border border-stone/40 rounded-lg p-3 text-xs text-mist max-h-40 overflow-auto">
                  {lightbox.prompt}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
